// Authenticator app (TOTP) enrolment and removal (FR-06, B2). Recovery codes
// are shown once, straight from the confirm response, and never cached.
import {
  Anchor,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  List,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { ME_KEY, useMe } from "../../shared/session/me";
import { refresh } from "../../shared/session/session";
import { useStepUp } from "../../shared/session/stepup";
import { ProofForm, useEnrolment, type Enrolment } from "../../shared/session/enrol";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice, WidePage } from "../../shared/ui/Page";
import { UssdPinSection } from "./UssdPinSection";

export function SecurityPage() {
  return (
    <SignedIn>
      <Security />
    </SignedIn>
  );
}

function Security() {
  const { t } = useTranslation();
  const me = useMe();
  const stepUp = useStepUp();
  const queryClient = useQueryClient();
  const { needsProof, begin } = useEnrolment();
  const f = useFormat();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const m = me.data;

  const start = async (password?: string) => {
    setError(null);
    setNotice(null);
    setBusy(true);
    const r = await begin(password);
    setBusy(false);
    if (r.error) return setError(r.error);
    if (!r.enrolment) return;
    setEnrolment(r.enrolment);
    setCode("");
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { data, error } = await api.POST("/mfa/totp/confirm", { body: { code } });
    if (!data) {
      setBusy(false);
      return setError(errorMessage(error, t("error.generic")));
    }
    setEnrolment(null);
    setCode("");
    setRecoveryCodes(data.recovery_codes);
    // A new session carries the "totp" factor, so pages that require it open up (B2).
    await refresh();
    setBusy(false);
    await queryClient.invalidateQueries();
  };

  const remove = async () => {
    setError(null);
    setNotice(null);
    const token = await stepUp("totp_disable");
    if (!token) return;
    const { response, error } = await api.DELETE("/mfa/totp", { params: { header: { "X-Step-Up-Token": token } } });
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("security.removed"));
    await queryClient.invalidateQueries({ queryKey: ME_KEY });
  };

  return (
    <WidePage title={t("security.title")}>
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Title order={2} size="h4">
              {t("security.totp_title")}
            </Title>
            {m && (
              <Badge color={m.totp_enabled ? "green" : "gray"} data-testid="totp-status">
                {m.totp_enabled ? t("security.totp_on") : t("security.totp_off")}
              </Badge>
            )}
          </Group>
          <Text size="sm">{t("security.totp_intro")}</Text>
          {notice && <Notice>{notice}</Notice>}
          {!m ? (
            me.isPending ? (
              <Loader size="sm" aria-label={t("common.loading")} />
            ) : (
              <FormError message={errorMessage(me.error, t("error.generic"))} />
            )
          ) : recoveryCodes ? (
            <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
          ) : m.totp_enabled ? (
            m.totp_required ? (
              <Text size="sm" c="dimmed" data-testid="totp-required">
                {t("security.required")}
              </Text>
            ) : (
              <Button variant="subtle" color="red" w="fit-content" onClick={() => void remove()}>
                {t("security.remove")}
              </Button>
            )
          ) : enrolment ? (
            <form onSubmit={confirm}>
              <Stack gap="sm">
                <Text size="sm">{t("security.step1")}</Text>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("security.link")}
                  </Text>
                  <Code block style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }} data-testid="otpauth-url">
                    {enrolment.otpauth_url}
                  </Code>
                  <Anchor href={enrolment.otpauth_url} size="sm">
                    {t("security.open_app")}
                  </Anchor>
                </Stack>
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    {t("security.secret")}
                  </Text>
                  <Group gap="xs">
                    <Code fz="md" data-testid="totp-secret" style={{ wordBreak: "break-all" }}>
                      {enrolment.secret}
                    </Code>
                    <CopyButton value={enrolment.secret}>
                      {({ copied, copy }) => (
                        <Button size="xs" variant="default" onClick={copy}>
                          {copied ? t("common.copied") : t("common.copy")}
                        </Button>
                      )}
                    </CopyButton>
                  </Group>
                </Stack>
                <Text size="sm">{t("security.step2")}</Text>
                <TextInput
                  label={t("security.code")}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value.trim())}
                  required
                />
                <FormError message={error} />
                <Group gap="xs">
                  <Button type="submit" loading={busy}>
                    {t("security.turn_on")}
                  </Button>
                  <Button variant="default" onClick={() => setEnrolment(null)}>
                    {t("common.cancel")}
                  </Button>
                </Group>
              </Stack>
            </form>
          ) : needsProof ? (
            <ProofForm busy={busy} onSubmit={(pw) => void start(pw)} />
          ) : (
            <Button w="fit-content" loading={busy} onClick={() => void start()}>
              {t("security.setup")}
            </Button>
          )}
          {m?.totp_enabled && m.totp_new_until && (
            <Text size="sm" c="dimmed" data-testid="totp-new">
              {t("security.new_app_hold", { date: f.dateTime(m.totp_new_until) })}
            </Text>
          )}
          {!enrolment && <FormError message={error} />}
        </Stack>
      </Paper>
      <UssdPinSection />
    </WidePage>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  return (
    <Stack gap="sm" data-testid="recovery-codes">
      <Notice>{t("security.enabled")}</Notice>
      <Title order={3} size="h5">
        {t("security.recovery_title")}
      </Title>
      <Text size="sm">{t("security.recovery_intro")}</Text>
      <List spacing={2} styles={{ root: { fontFamily: "monospace" } }}>
        {codes.map((c) => (
          <List.Item key={c}>{c}</List.Item>
        ))}
      </List>
      <Group gap="xs">
        <CopyButton value={codes.join("\n")}>
          {({ copied, copy }) => (
            <Button variant="default" onClick={copy}>
              {copied ? t("common.copied") : t("security.copy_codes")}
            </Button>
          )}
        </CopyButton>
        <Button onClick={onDone}>{t("security.saved")}</Button>
      </Group>
    </Stack>
  );
}
